import React, { useState, forwardRef, useRef } from "react";
import {
    TextField,
    MenuItem,
    FormControl,
    Menu,
    IconButton,
    InputAdornment
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { ThemeProvider } from "@emotion/react";
import CustomTimePickerTheme from "../../styles/CustomeTimePickerTheme";

const CustomTimePicker = forwardRef(
    (
        {
            type = "startTime",
            error,
            value,
            onChange,
            step = 15,
            outlinedInputSx = ""
        },
        ref
    ) => {
        const [menuWidth, setMenuWidth] = useState(0);
        const timePickerRef = useRef(null);
        const defaultTime =
            type === "startTime"
                ? new Date().setHours(9, 0, 0, 0)
                : type === "endTime"
                  ? new Date().setHours(17, 0, 0, 0)
                  : type === "startTimeBoundary"
                    ? new Date().setHours(7, 0, 0, 0)
                    : new Date().setHours(19, 0, 0, 0);

        // Format the time stamp into hh:mm A
        const formatTime = (timestamp) => {
            const date = new Date(timestamp);
            const hours = date.getHours();
            const minutes = date.getMinutes();
            if (hours === 23 && minutes === 59) return "Midnight";
            const period = hours >= 12 ? "PM" : "AM";
            const displayHours = hours % 12 || 12; // Convert 24-hour to 12-hour format
            const displayMinutes = minutes < 10 ? `0${minutes}` : minutes;
            return `${displayHours}:${displayMinutes} ${period}`;
        };

        const parseTime = (input) => {
            const regex = /^(\d{1,2})(?::(\d{2}))?\s?(AM|PM)?$/i;
            const match = input.trim().toUpperCase().match(regex);

            if (match) {
                let [, hour, minute = "00", period = "AM"] = match;

                hour = parseInt(hour, 10);
                minute = parseInt(minute, 10);

                if (hour === 12) hour = 0; // 12 AM is 00:00 in 24-hour format
                if (period === "PM") hour += 12;
                if (hour > 23) hour = 23;
                if (minute > 59) minute = 59;

                const date = new Date();
                date.setHours(hour, minute, 0, 0);
                return date.getTime();
            }

            return defaultTime; // Return default time if input is invalid
        };

        const [selectedTime, setSelectedTime] = useState(
            formatTime(value || defaultTime)
        );
        const [anchorEl, setAnchorEl] = useState(null);
        const [openPicker, setOpenPicker] = useState(false);

        const handleClick = (event) => {
            if (timePickerRef.current) {
                setMenuWidth(timePickerRef.current.offsetWidth);
            }
            setAnchorEl(event.currentTarget);
            setOpenPicker(true);
        };

        const handleClose = () => {
            setAnchorEl(null);
            setOpenPicker(false);
        };

        const handleInputChange = (event) => {
            const inputValue = event.target.value;
            const sanitizedValue = inputValue
                .replace(/[^0-9:APMapm\s]/g, "")
                .replace(/(AM|PM)/g, (match) => match.toUpperCase());
            const regex = /^(\d{1,2})(?::(\d{2}))?\s?(AM|PM)?$/i;
            const match = sanitizedValue.match(regex);

            if (match) {
                let [, hour, minute = "00", period = "AM"] = match;
                if (
                    period &&
                    sanitizedValue.includes("AM") &&
                    sanitizedValue.includes("PM")
                ) {
                    setSelectedTime(sanitizedValue);
                    return;
                }

                const timestamp = parseTime(sanitizedValue);
                setSelectedTime(sanitizedValue);
                if (onChange) onChange(timestamp);
            } else {
                setSelectedTime(sanitizedValue);
            }
        };

        const handleSelectChange = (time) => {
            const timestamp = parseTime(
                time === "Midnight" ? "11:59 PM" : time
            );
            const timeLabel = time === "Midnight" ? "Midnight" : time;
            setSelectedTime(timeLabel);
            handleClose();
            if (onChange) onChange(timestamp);
        };

        const handleBlur = () => {
            const timestamp =
                selectedTime === "Midnight"
                    ? parseTime("11:59 PM")
                    : parseTime(selectedTime);
            setSelectedTime(formatTime(timestamp));
            if (onChange) onChange(timestamp);
        };

        const times = [];
        const hoursInDay = 24;
        const minutesInHour = 60;

        // Calculate the total number of time slots based on step size
        const totalSlots = (minutesInHour / step) * hoursInDay;

        for (let i = 0; i < totalSlots; i++) {
            const date = new Date();
            const hours = Math.floor((i * step) / minutesInHour);
            const minutes = (i * step) % minutesInHour;

            date.setHours(hours);
            date.setMinutes(minutes);

            times.push(formatTime(date));
        }
        times.push("Midnight");

        return (
            <ThemeProvider theme={CustomTimePickerTheme}>
                <FormControl variant="outlined" ref={timePickerRef}>
                    <TextField
                        label={
                            type === "startTime"
                                ? "Start time"
                                : type === "endTime"
                                  ? "End time"
                                  : type === "startTimeBoundary"
                                    ? "Start time boundary"
                                    : "End time boundary"
                        }
                        sx={{
                            "& .MuiOutlinedInput-input": {
                                ...outlinedInputSx
                            }
                        }}
                        value={selectedTime}
                        onClick={handleClick}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={error}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={handleClick}
                                        size="small"
                                    >
                                        {openPicker ? (
                                            <ArrowDropUpIcon
                                                sx={{ marginRight: "-9px" }}
                                            />
                                        ) : (
                                            <ArrowDropDownIcon
                                                sx={{ marginRight: "-9px" }}
                                            />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <Menu
                        anchorEl={anchorEl}
                        open={openPicker}
                        onClose={handleClose}
                        PaperProps={{
                            style: {
                                width: menuWidth
                            }
                        }}
                    >
                        {times.map((time) => (
                            <MenuItem
                                key={time}
                                onClick={() => handleSelectChange(time)}
                                selected={time === selectedTime}
                            >
                                {time}
                            </MenuItem>
                        ))}
                    </Menu>
                </FormControl>
            </ThemeProvider>
        );
    }
);

export default CustomTimePicker;
